import { createAffiliateCommissionPanel } from '../affiliate/AffiliateCommissionPanel.jsx';
import {
  DEFAULT_LIMIT,
  buildReferralLink,
  copyText,
  fetchReferralStats,
  fetchReferredCustomers,
  formatMoney,
  formatSiteDateTime,
  shareReferralLink,
} from './helpers.js';
import { isAccountAbortError } from '../boot/accountCatalogCache.js';
import { BLOCKING_LOAD_STUCK_MS } from '../boot/playerLoading.js';
import { phrase } from '../i18n/language.js';

export function createReferralCommissionPage({ useState, useEffect, useLayoutEffect, useCallback, jsx, jsxs }) {
  const AffiliateCommission = createAffiliateCommissionPanel({
    useState,
    useEffect,
    jsx,
    jsxs,
  });

  function ReferralCommissionPage({ t, lang, onBack }) {
    const label = (key, vars) => (typeof t === 'function' ? t(key, vars) : key);
    const goBack = onBack || (() => window.history.back());
    const [stats, setStats] = useState(null);
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    const showNotice = (msg) => {
      setNotice(msg);
      window.setTimeout(() => setNotice(''), 1800);
    };

    const loadStats = useCallback(async () => {
      const next = await fetchReferralStats();
      setStats(next);
      return next;
    }, []);

    const loadPage = useCallback(async (nextPage, replace) => {
      const payload = await fetchReferredCustomers({ page: nextPage, limit: DEFAULT_LIMIT });
      setItems((prev) => (replace ? payload.items : prev.concat(payload.items)));
      setPage(payload.page);
      setTotal(payload.total);
      setTotalPages(payload.totalPages);
      return payload;
    }, []);

    useEffect(() => {
      let cancelled = false;
      const stuck = window.setTimeout(() => {
        if (!cancelled) setLoading(false);
      }, BLOCKING_LOAD_STUCK_MS);
      (async () => {
        setLoading(true);
        setError('');
        try {
          await Promise.all([loadStats(), loadPage(1, true)]);
        } catch (err) {
          if (!cancelled && !isAccountAbortError(err)) setError(err.message || phrase('Failed to load'));
        } finally {
          window.clearTimeout(stuck);
          if (!cancelled) setLoading(false);
        }
      })();
      return () => {
        cancelled = true;
        window.clearTimeout(stuck);
      };
    }, [loadStats, loadPage]);

    const code = String(stats?.referralCode || '').trim().toUpperCase();
    const link = buildReferralLink(code);
    const affiliate = stats?.customerType === 'affiliate';

    const onCopyCode = async () => {
      if (await copyText(code)) showNotice(label('profReferralCodeCopied'));
      else showNotice(label('profCopyFailed'));
    };

    const onCopyLink = async () => {
      if (await copyText(link)) showNotice(label('profReferralLinkCopied'));
      else showNotice(label('profCopyFailed'));
    };

    const onShare = async () => {
      const result = await shareReferralLink({
        title: label('profReferralShareTitle'),
        text: label('profReferralShareText', { code }),
        url: link,
      });
      if (result.ok) {
        showNotice(result.method === 'share' ? label('profShared') : label('profReferralLinkCopied'));
        return;
      }
      if (result.reason !== 'cancelled') showNotice(label('profCopyFailed'));
    };

    const headers = affiliate
      ? [
        label('refCustomer'),
        label('refRegistered'),
        label('refLastLogin'),
        label('refLastDeposit'),
        label('refTotalDeposit'),
        label('refTotalWithdrawal'),
        label('refCommissionPlus'),
        label('refCommissionMinus'),
        label('refNetCommission'),
      ]
      : [
        label('refCustomer'),
        label('refRegistered'),
        label('refLastLogin'),
        label('refLastDeposit'),
        label('refTotalDeposit'),
        label('refTotalWithdrawal'),
        label('refTotalBonus'),
      ];

    return jsx('div', {
      className: 'referral-page',
      'aria-label': label('profReferral'),
      children: jsxs('div', {
        className: 'rf-wrap',
        children: [
          jsxs('div', {
            className: 'rf-head',
            children: [
              jsx('button', {
                type: 'button',
                className: 'rf-back',
                onClick: goBack,
                children: `‹ ${label('toBack')}`,
              }),
              jsx('h1', { className: 'rf-title', children: label('profReferral') }),
            ],
          }),
          jsxs('section', {
            className: 'rf-section',
            children: [
              jsx('span', { className: 'rf-label', children: label('profReferralCode') }),
              jsx('p', { className: 'rf-code', children: code || '—' }),
              jsxs('div', {
                className: 'rf-actions',
                children: [
                  jsx('button', {
                    type: 'button',
                    className: 'rf-btn',
                    onClick: onCopyCode,
                    disabled: !code,
                    children: label('profCopyCode'),
                  }),
                ],
              }),
              jsx('span', { className: 'rf-label', style: { marginTop: 14 }, children: label('profReferralLink') }),
              jsx('p', { className: 'rf-link', children: link || '—' }),
              jsxs('div', {
                className: 'rf-actions',
                children: [
                  jsx('button', {
                    type: 'button',
                    className: 'rf-btn',
                    onClick: onCopyLink,
                    disabled: !link,
                    children: label('profCopyLink'),
                  }),
                  jsx('button', {
                    type: 'button',
                    className: 'rf-btn',
                    onClick: onShare,
                    disabled: !link,
                    children: label('profShare'),
                  }),
                ],
              }),
              notice ? jsx('p', { className: 'rf-notice', children: notice }) : null,
            ],
          }),
          jsx('section', {
            className: 'rf-section',
            children: loading
              ? null
              : affiliate
              ? jsx(AffiliateCommission, { t: label, stats, onRefresh: loadStats })
              : jsxs('dl', {
                className: 'rf-summary',
                children: [
                  jsxs('div', {
                    className: 'rf-stat',
                    children: [
                      jsx('dt', { children: label('profTotalReferrals') }),
                      jsx('dd', { children: Number(stats?.totalReferrals || 0) }),
                    ],
                  }),
                  jsxs('div', {
                    className: 'rf-stat',
                    children: [
                      jsx('dt', { children: label('profTotalReferralBonus') }),
                      jsx('dd', { children: formatMoney(stats?.totalReferralBonus ?? stats?.totalCommission, lang) }),
                    ],
                  }),
                ],
              }),
          }),
          jsxs('section', {
            className: 'rf-section',
            children: [
              jsx('h2', { className: 'rf-title', style: { fontSize: 18, marginBottom: 12 }, children: label('refCustomersHeading') }),
              error ? jsx('p', { className: 'rf-error', children: phrase(error) }) : null,
              loading
                ? jsx('div', { className: 'account-skel-line rf-skel', 'aria-hidden': 'true' })
                : null,
              !loading && items.length === 0
                ? jsx('p', { className: 'rf-empty', children: label('refEmpty') })
                : null,
              jsx('div', {
                className: 'rf-table-wrap',
                'aria-label': label('refCustomersHeading'),
                children: jsxs('table', {
                  className: 'rf-table',
                  children: [
                    jsx('thead', {
                      children: jsx('tr', {
                        children: headers.map((h) => jsx('th', { children: h }, h)),
                      }),
                    }),
                    jsx('tbody', {
                      children: items.map((row, idx) => {
                        const netNeg = Number(row.netCommission) < 0;
                        const lastDep = row.lastDepositAt
                          ? `${formatMoney(row.lastDepositAmount, lang)} · ${formatSiteDateTime(row.lastDepositAt, lang)}`
                          : formatMoney(row.lastDepositAmount, lang);
                        const cells = affiliate
                          ? [
                            row.displayName,
                            formatSiteDateTime(row.registeredAt, lang),
                            formatSiteDateTime(row.lastLogin, lang),
                            lastDep,
                            formatMoney(row.totalDeposit, lang),
                            formatMoney(row.totalWithdrawal, lang),
                            formatMoney(row.commissionPlus, lang),
                            formatMoney(row.commissionMinus, lang),
                            formatMoney(row.netCommission, lang),
                          ]
                          : [
                            row.displayName,
                            formatSiteDateTime(row.registeredAt, lang),
                            formatSiteDateTime(row.lastLogin, lang),
                            lastDep,
                            formatMoney(row.totalDeposit, lang),
                            formatMoney(row.totalWithdrawal, lang),
                            formatMoney(row.totalBonus, lang),
                          ];
                        return jsx('tr', {
                          children: cells.map((cell, i) => jsx('td', {
                            className: affiliate && i === cells.length - 1 && netNeg ? 'money-negative' : '',
                            children: cell,
                          }, i)),
                        }, `${row.displayName}-${idx}`);
                      }),
                    }),
                  ],
                }),
              }),
              page < totalPages
                ? jsx('button', {
                  type: 'button',
                  className: 'rf-btn rf-more',
                  onClick: () => loadPage(page + 1, false),
                  children: `${label('refLoadMore')} (${page}/${totalPages})`,
                })
                : null,
            ],
          }),
        ],
      }),
    });
  }

  return ReferralCommissionPage;
}
